import type { Direction, RouteSnapshot } from "@bus/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { getSnapshot, openLiveStream, type LiveConnectionStatus } from "./api";

export const SNAPSHOT_REFRESH_MS = 30_000;

export function useLiveSnapshot(routeId: string, direction: Direction) {
  const queryClient = useQueryClient();
  const queryKey = ["snapshot", routeId, direction] as const;
  const [connectionStatus, setConnectionStatus] = useState<LiveConnectionStatus>("connecting");
  const [lastSnapshotAt, setLastSnapshotAt] = useState(() => Date.now());
  const [newVehicleIds, setNewVehicleIds] = useState<Set<string>>(() => new Set());
  const knownVehicles = useRef(new Set<string>());
  const timers = useRef(new Map<string, number>());
  const query = useQuery({ queryKey, queryFn: async () => { const data = await getSnapshot(routeId, direction); knownVehicles.current = new Set(data.vehicles.map((vehicle) => vehicle.vehicleId)); setLastSnapshotAt(Date.now()); return data; }, enabled: Boolean(routeId), staleTime: 30_000, refetchInterval: 60_000 });

  useEffect(() => {
    if (!routeId) return;
    knownVehicles.current = new Set();
    setNewVehicleIds(new Set());
    setConnectionStatus("connecting");
    const close = openLiveStream(routeId, direction, (event) => {
      if (event.type === "snapshot" && (event.data.route.id !== routeId || event.data.stops[0]?.direction !== direction)) return;
      if ((event.type === "position" || event.type === "eta") && (event.data.routeId !== routeId || event.data.direction !== direction)) return;
      if (event.type === "snapshot") {
        knownVehicles.current = new Set(event.data.vehicles.map((vehicle) => vehicle.vehicleId));
        setLastSnapshotAt(Date.now());
        queryClient.setQueryData(queryKey, event.data);
      }
      if (event.type === "position") {
        if (!knownVehicles.current.has(event.data.vehicleId)) {
          knownVehicles.current.add(event.data.vehicleId);
          setNewVehicleIds((current) => new Set(current).add(event.data.vehicleId));
          const previous = timers.current.get(event.data.vehicleId); if (previous) window.clearTimeout(previous);
          timers.current.set(event.data.vehicleId, window.setTimeout(() => setNewVehicleIds((current) => { const next = new Set(current); next.delete(event.data.vehicleId); return next; }), 6_000));
        }
        queryClient.setQueryData<RouteSnapshot>(queryKey, (current) => current ? { ...current, vehicles: [...current.vehicles.filter((vehicle) => vehicle.vehicleId !== event.data.vehicleId), event.data], generatedAt: event.data.receivedAt } : current);
      }
      if (event.type === "eta") queryClient.setQueryData<RouteSnapshot>(queryKey, (current) => current ? { ...current, etas: [...current.etas.filter((eta) => !(eta.stopId === event.data.stopId && eta.direction === event.data.direction)), event.data], generatedAt: event.data.updatedAt } : current);
    }, setConnectionStatus);
    return () => { close(); timers.current.forEach((timer) => window.clearTimeout(timer)); timers.current.clear(); };
  }, [direction, queryClient, routeId]);
  return { ...query, connectionStatus, lastSnapshotAt, newVehicleIds };
}
